import {autoRegister, resolve} from "../../container";
import {Configuration} from "../../common/config";
import {Load} from "../../common/load";
import {GLOBAL, type GlobalWindow} from "../../common/globals";

export type SecuredRequestExecutorWindow = GlobalWindow & {
    AwsWafIntegration?: {
        getToken: () => Promise<string>;
    };
}

export interface SecuredExecutorResponse {
    getStatus: () => number;
    getHeader: (name: string) => string | undefined;
}

const X_AMZN_WAF_ACTION_HEADER = "x-amzn-waf-action";
const WAF_ACTION_CHALLENGE = "challenge";
const WAF_CHALLENGE_STATUS = 202;

@autoRegister()
export class SecuredRequestExecutor {
    private wafIntegrationUrl?: string;
    private window: SecuredRequestExecutorWindow;

    public constructor(
        configuration: Configuration = resolve(Configuration),
        private load: Load = resolve(Load)
    ) {
        this.wafIntegrationUrl = configuration.get("WAF_INTEGRATION_URL");
        this.window = GLOBAL.window();
    }

    public async execute<T extends Response | SecuredExecutorResponse>(securedRequest: () => Promise<T>): Promise<T> {
        const response = await securedRequest();
        if (!this.wafIntegrationUrl) {
            return response;
        }
        if (this.isChallengeRequired(response)) {
            await this.load.script(`${this.wafIntegrationUrl}/challenge.js`);
            await this.window.AwsWafIntegration?.getToken();
            return await securedRequest();
        }
        return response;
    }

    private isChallengeRequired(response: Response | SecuredExecutorResponse): boolean {
        if (this.isFetchResponse(response)) {
            return response.status === WAF_CHALLENGE_STATUS && response.headers.get(X_AMZN_WAF_ACTION_HEADER) === WAF_ACTION_CHALLENGE;
        } else {
            return response.getStatus() === WAF_CHALLENGE_STATUS && response.getHeader(X_AMZN_WAF_ACTION_HEADER) === WAF_ACTION_CHALLENGE;
        }
    }

    private isFetchResponse(response: Response | SecuredExecutorResponse): response is Response {
        const fetchResponse = response as Response;
        return fetchResponse.headers !== undefined && fetchResponse.status !== undefined;
    }
}
